import { computed, Injectable, inject, resource } from '@angular/core';
import { Session } from '../session/session';
import type { BrandSlug } from '../shared/brand-slug';
import type { OrgId, UserId } from '../shared/ids';
import { resourceSettler } from '../shared/resource-settled';
import type { Organization, OrgMember } from './organization';
import { isAdmin, isSuperAdmin, periodEnded } from './organization';
import { OrganizationRepository } from './organization-repository';

/** Org data together with the user it was loaded for. */
type OrgSnapshot = {
  readonly userId: UserId;
  readonly orgs: readonly Organization[];
  readonly memberships: readonly OrgMember[];
};

const NO_ORGS: readonly Organization[] = [];
const NO_MEMBERS: readonly OrgMember[] = [];

/**
 * Organization state derived from the Session.
 *
 * The user and their member org ids are the resource's parameter, so the data
 * is keyed by identity by construction: a sign-in as a different user (or a
 * sign-out) discards the previous value synchronously and reloads; nothing has
 * to be reset in sequence. `loaded()` therefore always means "loaded for the
 * current user, with the current org known".
 */
@Injectable({ providedIn: 'root' })
export class OrganizationStore {
  private readonly repo = inject(OrganizationRepository);
  private readonly session = inject(Session);

  private readonly snapshot = resource<
    OrgSnapshot,
    { userId: UserId; orgIds: readonly OrgId[] } | undefined
  >({
    params: () => {
      const userId = this.session.userId();
      const orgIds = this.session.orgIds();
      return userId && orgIds ? { userId, orgIds } : undefined;
    },
    loader: async ({ params: { userId } }) => {
      const { orgs, memberships } = await this.repo.forUser(userId);
      return { userId, orgs, memberships };
    },
  });

  /**
   * Resolves once the current user's organizations are loaded (or immediately
   * for an anonymous viewer); rejects with the Session or repository error if
   * a load failed. For callers that sequence routing decisions on org data.
   */
  readonly settled = resourceSettler(this.snapshot, { after: () => this.session.settled() });

  readonly loaded = computed(() => this.snapshot.hasValue());
  /** Why the current user's organizations failed to load; null while they load or hold. */
  readonly error = computed<Error | null>(
    () => this.snapshot.error() ?? this.session.error() ?? null
  );
  readonly availableOrgs = computed<readonly Organization[]>(() =>
    this.snapshot.hasValue() ? this.snapshot.value().orgs : NO_ORGS
  );
  readonly memberships = computed<readonly OrgMember[]>(() =>
    this.snapshot.hasValue() ? this.snapshot.value().memberships : NO_MEMBERS
  );

  readonly currentOrg = computed<Organization | null>(() => {
    const id = this.session.currentOrgId();
    return this.availableOrgs().find((o) => o.id === id) ?? null;
  });

  readonly currentMember = computed<OrgMember | null>(() => {
    const id = this.currentOrg()?.id;
    if (!id) return null;
    return this.memberships().find((m) => m.orgId === id) ?? null;
  });

  readonly subscription = computed(() => this.currentOrg()?.subscription ?? 'pending');
  readonly periodEnded = computed(() => {
    const org = this.currentOrg();
    if (!org) return false;
    return periodEnded(org);
  });

  readonly isAdmin = computed(() => {
    const member = this.currentMember();
    return member ? isAdmin(member) : false;
  });

  readonly isSuperAdmin = computed(() => {
    const member = this.currentMember();
    return member ? isSuperAdmin(member) : false;
  });

  readonly ownedBrandSlugs = computed<readonly BrandSlug[]>(() =>
    Array.from(this.currentOrg()?.buckets.own ?? new Set<BrandSlug>())
  );

  readonly competitorBrandSlugs = computed<readonly BrandSlug[]>(() =>
    Array.from(this.currentOrg()?.buckets.competitor ?? new Set<BrandSlug>())
  );
}
